/**
 * The origin that served this script — used as the widget's default API base so
 * the embed snippet needs no `data-api-base`.
 *
 * MUST be read at module-load time: `document.currentScript` is only set while
 * the script is executing, not later inside `connectedCallback`/event handlers.
 */
export function deriveWidgetOrigin(doc?: Document): string {
  if (!doc) return "";
  const current = doc.currentScript as HTMLScriptElement | null;
  const src = current?.src || findWidgetSrc(doc);
  if (!src) return "";
  try {
    return new URL(src).origin;
  } catch {
    return "";
  }
}

/** Fallback when currentScript is unavailable: the last <script> ending in /widget.js. */
function findWidgetSrc(doc: Document): string {
  const scripts = Array.from(doc.querySelectorAll("script[src]")) as HTMLScriptElement[];
  for (let i = scripts.length - 1; i >= 0; i--) {
    if (/\/widget\.js(\?|$)/.test(scripts[i].src)) return scripts[i].src;
  }
  return "";
}

/** Captured ONCE at module load (currentScript is null later). The lazy report
 * imports this instead of calling deriveWidgetOrigin() at render time. */
export const WIDGET_ORIGIN: string =
  typeof document !== "undefined" ? deriveWidgetOrigin(document) : "";
